/**
 * Client-side passkey helpers built on `@simplewebauthn/browser`. The
 * server returns simplewebauthn-shaped options; we hand them to the
 * browser library which calls `navigator.credentials.{create,get}`
 * and returns the response in the shape the server's verify step
 * expects.
 *
 * No new abstractions here — just two thin wrappers that own the
 * begin → ceremony → finish pattern so the route + Settings UIs stay
 * declarative.
 */
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api } from './api';

export interface RegisterBeginResponse {
  options: PublicKeyCredentialCreationOptionsJSON;
  challengeId: string;
}
export interface AuthBeginResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
  challengeId: string;
}

/** Returns false on browsers without WebAuthn (very old iOS Safari,
 *  some embedded WebViews). Use this to hide passkey buttons. */
export function isSupported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Register a passkey for the currently signed-in user. The caller is
 * already authenticated (PIN, email OTP, etc.) — this attaches a new
 * credential to that member so future sign-ins are biometric. The
 * `label` is what shows up in Settings → Signed-in devices.
 */
export async function registerPasskey(label?: string): Promise<void> {
  if (!isSupported()) throw new Error('passkeys are not supported on this device');
  const { options, challengeId } = await api.post<RegisterBeginResponse>(
    '/auth/passkey/register-begin',
  );
  const response = await startRegistration({ optionsJSON: options });
  await api.post('/auth/passkey/register-finish', {
    challengeId,
    response,
    ...(label ? { label } : {}),
  });
}

/**
 * Sign in via passkey using a discoverable credential — the user
 * never types a display name or email; the authenticator's account
 * picker decides. Throws if the user dismisses the prompt OR if the
 * credential / signature doesn't verify server-side.
 */
export async function signInWithPasskey(): Promise<void> {
  if (!isSupported()) throw new Error('passkeys are not supported on this device');
  const { options, challengeId } = await api.post<AuthBeginResponse>(
    '/auth/passkey/sign-in-begin',
  );
  const response = await startAuthentication({ optionsJSON: options });
  await api.post('/auth/passkey/sign-in-finish', { challengeId, response });
}
